import { CommonModule } from '@angular/common';
import { Component, inject, signal, ViewChild } from '@angular/core';

import { ButtonDirective } from '../../button/button.directive';
import { InputComponent } from '../../input/input.component';
import { tableFiltersComponent } from './table-filters/table-filters.component';
import { NgIcon } from '@ng-icons/core';
import { TableData } from '../table.interface';
import { ReportsService } from '../../../services/reports.service';
import { TableContext } from '../../../services/table/table-context.service';
import { DictionaryService } from '../../../services/dictionary.service';
import { ExportButtonComponent } from '../../export-button/export-button.component';

@Component({
  selector: 'app-table-header',
  imports: [
    CommonModule,
    ButtonDirective,
    InputComponent,
    NgIcon,
    tableFiltersComponent,
    ExportButtonComponent,
  ],
  templateUrl: './table-header.component.html',
  styleUrl: './table-header.component.scss',
})
export class TableHeaderComponent {
  // ----------- outputs ---------------------------

  private searchTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

  // ----------- Services ---------------------------

  reportsService = inject(ReportsService);
  tableContext = inject(TableContext);
  dictionaryService = inject(DictionaryService);
  // ------------ used vars from context ------------

  canSelect = this.tableContext.canSelect;
  selectOptions = this.tableContext.selectOptions;

  // child components
  @ViewChild('filtersComponent') filtersComponent!: tableFiltersComponent;

  get ctx() {
    return {
      onClick: this.handleSelectAction,
      disabled: !this.tableContext.selectedRows().length,
    };
  }

  exportLoading = signal<boolean>(false);
  exportTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
  // ----------- functions --------------------------

  /**
   * @description exports table data to excel sheet
   */
  exportData = () => {
    this.exportTimeout = setTimeout(() => {
      this.exportLoading.set(true);
    }, 250);

    this.tableContext.getAllData().subscribe((allData: TableData) => {
      console.log({ allData });
      console.log(this.tableContext.name());
      this.reportsService.exportTableData(allData, this.tableContext.name());
      clearTimeout(this.exportTimeout);
      this.exportLoading.set(false);
    });
  };

  search = (query: string) => {
    // console.time('search');
    this.tableContext.setIsLoading(true);

    const filters = this.tableContext._appliedFilters();

    this.tableContext.setSearchQuery(query);
    this.tableContext.dataProvider
      .fetchData(this.tableContext.buildRequest({
        search: query !== '' ? query : undefined,
        page: 1,
        pageSize: this.tableContext.pageSize,
        getTotalCount: true,
        filters,
      }))
      .subscribe({
        next: (res) => {
          this.tableContext.updateTableData(res);
          this.tableContext.setIsLoading(false);
        },
      });
  };

  /**
   * @description delays search query to avoid multiple requests
   * @param {string} query - search query
   */
  debounceSearch = (query: string, skipTimeout: boolean = false) => {
    // console.time('searchTimeout');
    clearTimeout(this.searchTimeout);

    if (!skipTimeout) {
      this.searchTimeout = setTimeout(() => {
        this.search(query);
      }, 500);
    } else {
      this.search(query);
    }
    // console.timeEnd('searchTimeout');
  };

  handleSelectAction = () => {
    const selectedRows = this.tableContext
      .tableData()
      .rows.filter((row) => row.selected);
    this.selectOptions()?.onSelect(selectedRows);
    this.tableContext.handleAllRowsChecked(false); //clears the selection after action
  };

  handleResetFilters = () => {
    this.tableContext.reset();
    this.filtersComponent.resetAllFilters();
  };
}
